import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import CompetencyTypeTable from '../../../components/tables/CompetencyTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useCompetencyTypeSearch, useDeleteCompetencyType } from '../../../hooks/useCompetencyTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { CompetencyTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const CompetencyTypeList: React.FC = () => {
  const [items, setItems] = useState<CompetencyTypeDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Direct API search (no caching)
  const { search } = useCompetencyTypeSearch();
  const deleteCompetencyType = useDeleteCompetencyType();

  // Search state persistence
  const {
    currentPage,
    pageSize,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSearchFilters,
    clearSearchState,
  } = useSearchState('competencytypes', {
    currentPage: 1,
    pageSize: 10,
    searchFilters: {},
  });

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competency Types', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
      return response.totalCount;
    } catch (error) {
      console.error('Error fetching Competency Types:', error);
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: CompetencyTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteCompetencyType.mutateAsync(dto.id || '');
      // Refetch after delete
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting CompetencyType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/competencytypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.CompetencyType.View"
    >
      <Card
        title="Manage Competency Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/competencytypes/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <CompetencyTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/competencytypes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default CompetencyTypeList;
